import React, { useState, useEffect } from "react";
import {
  FaCube,
  FaLayerGroup,
  FaPalette,
  FaPuzzlePiece,
  FaRuler,
  FaPlus,
  FaTrash,
  FaDollarSign,
  FaCalculator,
  FaExclamationTriangle,
  FaTimes,
  FaSpinner,
  FaBoxes,
  FaShoppingBasket
} from "react-icons/fa";
import { getData } from "../../services/api.service";
import { getStockProducts } from "../../services/stockLocalService";

function OrderItemBuilder({ 
  item = null, 
  onSave, 
  onCancel, 
  skus = [], 
  materials = [], 
  colors = [], 
  accessories = [],
  isEditing = false 
}) {
  const [formData, setFormData] = useState({
    item_id: null, // Para items existentes
    sku_id: '',
    quantity: 1,
    sale_price: '',
    details: {
      material_id: '',
      measurements: '',
      accessories: [],
      colors: []
    },
    // Productos del stock local
    stock_products: [] // Array de { product_id, quantity, unit_price }
  });

  const [errors, setErrors] = useState({});
  const [selectedSku, setSelectedSku] = useState(null);
  const [totalPrice, setTotalPrice] = useState(0);
  
  // Estados para referencias filtradas
  const [filteredMaterials, setFilteredMaterials] = useState([]);
  const [filteredColors, setFilteredColors] = useState([]);
  const [filteredMeasures, setFilteredMeasures] = useState([]);
  const [filteredAccessories, setFilteredAccessories] = useState([]);
  const [loadingReferences, setLoadingReferences] = useState(false);
  const [customMeasurement, setCustomMeasurement] = useState('');
  
  // Estados para productos de stock local
  const [stockProducts, setStockProducts] = useState([]);
  const [loadingStock, setLoadingStock] = useState(false);
  const [selectedStockProduct, setSelectedStockProduct] = useState('');
  const [stockProductQuantity, setStockProductQuantity] = useState(1);

  useEffect(() => {
    if (item) {
      // Cargar datos del ítem para edición
      // Combinar accessory_id (legacy) con additional_accessories en un solo array
      const legacyAccessoryId = item.details?.accessory_id;
      const additionalAccessories = item.details?.additional_accessories || [];
      const allAccessories = legacyAccessoryId 
        ? [...additionalAccessories, legacyAccessoryId].filter((v, i, a) => a.indexOf(v) === i) // remover duplicados
        : additionalAccessories;
      
      setFormData({
        item_id: item.item_id, // IMPORTANTE: Preservar item_id para updates
        sku_id: item.sku_id || '',
        quantity: item.quantity || 1,
        sale_price: item.sale_price || '',
        details: {
          material_id: item.details?.material_id || '',
          measurements: item.details?.measurements || '',
          accessories: allAccessories,
          colors: item.details?.colors || []
        },
        stock_products: item.stock_products || []
      });
      setCustomMeasurement(item.details?.measurements || '');
    }
  }, [item]);

  // Cargar productos de stock local
  useEffect(() => {
    const loadStockProducts = async () => {
      try {
        setLoadingStock(true);
        const response = await getStockProducts(false);
        if (response.data && response.data.success) {
          setStockProducts(response.data.data || []);
        }
      } catch (error) {
        console.error('Error cargando productos de stock:', error);
      } finally {
        setLoadingStock(false);
      }
    };

    loadStockProducts();
  }, []);

  // Cargar referencias cuando se selecciona un SKU
  useEffect(() => {
    const loadSkuReferences = async () => {
      if (!formData.sku_id) {
        setFilteredMaterials([]);
        setFilteredMeasures([]);
        setFilteredAccessories([]);
        setFilteredColors([]);
        return;
      }

      setLoadingReferences(true);

      try {
        const response = await getData(`/api/admin/sku/${formData.sku_id}/references`);
        
        if (response.data.success) {
          const { materials: refMaterials, measures, accessories: refAccessories } = response.data.data;

          setFilteredMaterials(refMaterials);
          setFilteredMeasures(measures);
          setFilteredAccessories(refAccessories);

          // Si el material actual no está en la lista filtrada, limpiarlo
          if (formData.details.material_id) {
            const materialExists = refMaterials.some(m => m.id === parseInt(formData.details.material_id));
            if (!materialExists) {
              setFormData(prev => ({
                ...prev,
                details: {
                  ...prev.details,
                  material_id: '',
                  colors: []
                }
              }));
              setFilteredColors([]);
            }
          }
        }
      } catch (error) {
        // En caso de error, usar todas las opciones disponibles
        setFilteredMaterials(materials);
        setFilteredAccessories(accessories);
        setFilteredMeasures([]);
      } finally {
        setLoadingReferences(false);
      }
    };

    loadSkuReferences();
  }, [formData.sku_id]);

  // Cargar colores cuando se selecciona un material
  useEffect(() => {
    const loadMaterialColors = async () => {
      if (!formData.details.material_id) {
        setFilteredColors([]);
        return;
      }

      try {
        const response = await getData(`/api/admin/material/${formData.details.material_id}/colors`);
        
        if (response.data.success) {
          const materialColors = response.data.data;
          setFilteredColors(materialColors);

          // Limpiar colores seleccionados que ya no son válidos
          if (formData.details.colors.length > 0) {
            const validColorIds = materialColors.map(c => c.id);
            const validSelectedColors = formData.details.colors.filter(colorId => 
              validColorIds.includes(colorId)
            );
            
            if (validSelectedColors.length !== formData.details.colors.length) {
              setFormData(prev => ({
                ...prev,
                details: {
                  ...prev.details,
                  colors: validSelectedColors
                }
              }));
            }
          }
        }
      } catch (error) {
        // En caso de error, usar todos los colores disponibles
        setFilteredColors(colors);
      }
    };

    loadMaterialColors();
  }, [formData.details.material_id]);

  useEffect(() => {
    // Encontrar SKU seleccionado
    if (formData.sku_id) {
      const sku = skus.find(s => s.id.toString() === formData.sku_id.toString());
      setSelectedSku(sku);
    } else {
      setSelectedSku(null);
    }
  }, [formData.sku_id, skus]);

  useEffect(() => {
    // Calcular precio total (producto principal + productos de stock)
    const productPrice = parseFloat(formData.sale_price) || 0;
    const quantity = parseInt(formData.quantity) || 0;
    const productTotal = productPrice * quantity;
    
    // Sumar productos de stock local
    const stockTotal = formData.stock_products.reduce((sum, sp) => {
      return sum + (parseFloat(sp.unit_price) * parseInt(sp.quantity));
    }, 0);
    
    setTotalPrice(productTotal + stockTotal);
  }, [formData.sale_price, formData.quantity, formData.stock_products]);

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    
    if (name.startsWith('details.')) {
      const detailField = name.split('.')[1];
      
      // Si se cambia el material, limpiar los colores seleccionados
      if (detailField === 'material_id') {
        setFormData(prev => ({
          ...prev,
          details: {
            ...prev.details,
            material_id: value,
            colors: [] // Limpiar colores al cambiar material
          }
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          details: {
            ...prev.details,
            [detailField]: value
          }
        }));
      }
    } else {
      // Si se cambia el SKU, limpiar material, colores y accesorios
      if (name === 'sku_id') {
        setFormData(prev => ({
          ...prev,
          sku_id: value,
          details: {
            ...prev.details,
            material_id: '',
            colors: [],
            accessories: [],
            measurements: ''
          }
        }));
        setCustomMeasurement('');
      } else if (name === 'sale_price') {
        // Manejar precio como texto, permitir solo números y punto decimal
        const sanitizedValue = value.replace(/[^0-9.]/g, '');
        // Permitir solo un punto decimal
        const parts = sanitizedValue.split('.');
        const finalValue = parts.length > 2 
          ? parts[0] + '.' + parts.slice(1).join('') 
          : sanitizedValue;
        
        setFormData(prev => ({
          ...prev,
          sale_price: finalValue
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [name]: type === 'number' ? (value === '' ? '' : Number(value)) : value
        }));
      }
    }
    
    // Limpiar error del campo cuando se modifica
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleMeasurementSelect = (e) => {
    const selectedMeasure = e.target.value;
    setCustomMeasurement(selectedMeasure);
    setFormData(prev => ({
      ...prev,
      details: {
        ...prev.details,
        measurements: selectedMeasure
      }
    }));
  };

  const handleCustomMeasurementChange = (e) => {
    const value = e.target.value;
    setCustomMeasurement(value);
    setFormData(prev => ({
      ...prev,
      details: {
        ...prev.details,
        measurements: value
      }
    }));
  };

  const handleColorChange = (colorId) => {
    const colorIdNum = parseInt(colorId);
    setFormData(prev => ({
      ...prev,
      details: {
        ...prev.details,
        colors: prev.details.colors.includes(colorIdNum)
          ? prev.details.colors.filter(id => id !== colorIdNum)
          : [...prev.details.colors, colorIdNum]
      }
    }));
  };

  const handleAccessoryChange = (accessoryId) => {
    const accessoryIdNum = parseInt(accessoryId);
    setFormData(prev => ({
      ...prev,
      details: {
        ...prev.details,
        accessories: prev.details.accessories.includes(accessoryIdNum)
          ? prev.details.accessories.filter(id => id !== accessoryIdNum)
          : [...prev.details.accessories, accessoryIdNum]
      }
    }));
  };

  // Funciones para manejar productos de stock local
  const handleAddStockProduct = () => {
    if (!selectedStockProduct) return;
    
    const product = stockProducts.find(p => p.id === parseInt(selectedStockProduct));
    if (!product) return;
    
    // Verificar si el producto ya fue agregado
    const alreadyAdded = formData.stock_products.some(sp => sp.product_id === product.id);
    if (alreadyAdded) {
      alert('Este producto ya fue agregado');
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      stock_products: [...prev.stock_products, {
        product_id: product.id,
        product_name: product.product_name,
        quantity: stockProductQuantity,
        unit_price: product.unit_price
      }]
    }));
    
    // Resetear selección
    setSelectedStockProduct('');
    setStockProductQuantity(1);
  };

  const handleRemoveStockProduct = (productId) => {
    setFormData(prev => ({
      ...prev,
      stock_products: prev.stock_products.filter(sp => sp.product_id !== productId)
    }));
  };

  const handleStockProductQuantityChange = (productId, newQuantity) => {
    setFormData(prev => ({
      ...prev,
      stock_products: prev.stock_products.map(sp => 
        sp.product_id === productId 
          ? { ...sp, quantity: parseInt(newQuantity) || 1 }
          : sp
      )
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Validación: debe tener SKU O productos de stock (al menos uno)
    const hasSku = formData.sku_id && formData.sku_id !== '' && formData.sku_id !== null;
    const hasStockProducts = formData.stock_products && formData.stock_products.length > 0;
    
    if (!hasSku && !hasStockProducts) {
      newErrors.sku_id = 'Debe seleccionar un SKU o agregar productos de stock';
      newErrors.stock_products = 'Debe agregar al menos un producto de stock si no selecciona un SKU';
    }
    
    // Si tiene SKU, validar campos relacionados
    if (hasSku) {
      if (!formData.quantity || formData.quantity < 1) {
        newErrors.quantity = 'La cantidad debe ser mayor a 0';
      }

      if (!formData.sale_price || formData.sale_price <= 0) {
        newErrors.sale_price = 'El precio debe ser mayor a 0';
      }

      if (!formData.details.material_id) {
        newErrors['details.material_id'] = 'Debe seleccionar un material';
      }

      if (!formData.details.measurements || !formData.details.measurements.trim()) {
        newErrors['details.measurements'] = 'Debe especificar las medidas';
      }
    }
    
    // Si NO tiene SKU pero tiene productos de stock, validar que tenga al menos un producto
    if (!hasSku && !hasStockProducts) {
      newErrors.general = 'Debe seleccionar un SKU o agregar productos del stock local';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    const hasSku = formData.sku_id && formData.sku_id !== '' && formData.sku_id !== null;

    const itemData = {
      ...formData,
      // Si tiene SKU, incluir campos del producto
      sku_id: hasSku ? formData.sku_id : null,
      sale_price: hasSku ? parseFloat(formData.sale_price) || 0 : 0,
      quantity: hasSku ? parseInt(formData.quantity) || 1 : 0,
      details: hasSku ? {
        ...formData.details,
        material_id: parseInt(formData.details.material_id) || null,
        accessory_id: formData.details.accessory_id ? parseInt(formData.details.accessory_id) : null,
        additional_accessories: formData.details.additional_accessories,
        colors: formData.details.colors
      } : {
        material_id: null,
        measurements: '',
        accessory_id: null,
        additional_accessories: [],
        colors: []
      },
      stock_products: formData.stock_products
    };

    onSave(itemData);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  return (
    <div className="order-item-builder">
      <div className="card">
        <div className="card-header">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="card-title mb-0">
              <FaCube className="me-2 text-primary" />
              {isEditing ? 'Editar Ítem' : 'Nuevo Ítem'}
            </h5>
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={onCancel}
              type="button"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="card-body">
            <div className="row">
              {/* SKU Selection */}
              <div className="col-md-6 mb-3">
                <label className="form-label">
                  <FaCube className="me-1" />
                  SKU {formData.stock_products.length === 0 && '*'}
                </label>
                <select
                  className={`form-select ${errors.sku_id ? 'is-invalid' : ''}`}
                  name="sku_id"
                  value={formData.sku_id}
                  onChange={handleInputChange}
                >
                  <option value="">Seleccionar SKU (opcional si agrega productos de stock)</option>
                  {skus.map(sku => (
                    <option key={sku.id} value={sku.id}>
                      {sku.sku_description || sku.sku_name || `SKU #${sku.id}`}
                    </option>
                  ))}
                </select>
                {errors.sku_id && <div className="invalid-feedback">{errors.sku_id}</div>}
                {selectedSku && (
                  <div className="form-text">
                    <strong>Categoría:</strong> {selectedSku.category_name}
                  </div>
                )}
                {loadingReferences && (
                  <div className="form-text text-primary">
                    <FaSpinner className="fa-spin me-1" />
                    Cargando materiales, medidas y accesorios disponibles...
                  </div>
                )}
              </div>

              {/* Quantity */}
              <div className="col-md-3 mb-3">
                <label className="form-label">Cantidad {formData.sku_id && '*'}</label>
                <input
                  type="number"
                  className={`form-control ${errors.quantity ? 'is-invalid' : ''}`}
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleInputChange}
                  min="1"
                  disabled={!formData.sku_id}
                />
                {errors.quantity && <div className="invalid-feedback">{errors.quantity}</div>}
              </div>

              {/* Sale Price */}
              <div className="col-md-3 mb-3">
                <label className="form-label">
                  <FaDollarSign className="me-1" />
                  Precio Unitario {formData.sku_id && '*'}
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  className={`form-control ${errors.sale_price ? 'is-invalid' : ''}`}
                  name="sale_price"
                  value={formData.sale_price}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  disabled={!formData.sku_id}
                />
                {errors.sale_price && <div className="invalid-feedback">{errors.sale_price}</div>}
              </div>
            </div>

            {/* Price Calculation */}
            {(formData.quantity > 0 && formData.sale_price > 0) && (
              <div className="alert alert-info">
                <FaCalculator className="me-2" />
                <strong>Total del ítem:</strong> {formatCurrency(totalPrice)}
                <span className="ms-3">
                  ({formData.quantity} × {formatCurrency(formData.sale_price)})
                </span>
              </div>
            )}

            <hr />

            <h6 className="mb-3">
              <FaLayerGroup className="me-2" />
              Detalles del Producto
            </h6>

            <div className="row">
              {/* Material */}
              <div className="col-md-6 mb-3">
                <label className="form-label">
                  <FaLayerGroup className="me-1" />
                  Material {formData.sku_id && '*'}
                </label>
                <select
                  className={`form-select ${errors['details.material_id'] ? 'is-invalid' : ''}`}
                  name="details.material_id"
                  value={formData.details.material_id}
                  onChange={handleInputChange}
                  disabled={!formData.sku_id || loadingReferences}
                >
                  <option value="">
                    {!formData.sku_id 
                      ? 'Primero seleccione un SKU' 
                      : loadingReferences 
                        ? 'Cargando materiales...' 
                        : 'Seleccionar Material'}
                  </option>
                  {(filteredMaterials.length > 0 ? filteredMaterials : materials).map(material => (
                    <option key={material.id} value={material.id}>
                      {material.material_name}
                    </option>
                  ))}
                </select>
                {errors['details.material_id'] && <div className="invalid-feedback">{errors['details.material_id']}</div>}
                {formData.sku_id && filteredMaterials.length > 0 && (
                  <div className="form-text text-success">
                    ✓ {filteredMaterials.length} material(es) disponible(s) para este SKU
                  </div>
                )}
              </div>

              {/* Measurements - Híbrido: Dropdown + Campo de texto */}
              <div className="col-md-6 mb-3">
                <label className="form-label">
                  <FaRuler className="me-1" />
                  Medidas {formData.sku_id && '*'}
                </label>
                
                {/* Dropdown de medidas predefinidas (si existen) */}
                {filteredMeasures.length > 0 && (
                  <select
                    className="form-select mb-2"
                    value={customMeasurement}
                    onChange={handleMeasurementSelect}
                    disabled={!formData.sku_id || loadingReferences}
                  >
                    <option value="">Seleccione una medida predefinida o escriba una personalizada</option>
                    {filteredMeasures.map((measure, index) => (
                      <option key={measure.id || index} value={measure.measure_description}>
                        {measure.measure_description}
                      </option>
                    ))}
                  </select>
                )}
                
                {/* Campo de texto para medida personalizada (siempre visible) */}
                <input
                  type="text"
                  className={`form-control ${errors['details.measurements'] ? 'is-invalid' : ''}`}
                  name="details.measurements"
                  value={customMeasurement}
                  onChange={handleCustomMeasurementChange}
                  placeholder={filteredMeasures.length > 0 ? "O escriba una medida personalizada" : "Ej: 120x60x40"}
                  disabled={!formData.sku_id || loadingReferences}
                />
                {errors['details.measurements'] && <div className="invalid-feedback">{errors['details.measurements']}</div>}
                <div className="form-text">
                  {filteredMeasures.length > 0 
                    ? `${filteredMeasures.length} medida(s) predefinida(s) disponible(s) o escriba una personalizada`
                    : 'Formato sugerido: largo x ancho x alto (en cm)'}
                </div>
              </div>
            </div>

            {/* Accesorios */}
            {((filteredAccessories.length > 0 ? filteredAccessories : accessories).length > 0) && (
              <div className="mb-3">
                <label className="form-label">
                  <FaPuzzlePiece className="me-1" />
                  Accesorios
                </label>
                {!formData.sku_id && (
                  <div className="alert alert-info">
                    <FaExclamationTriangle className="me-2" />
                    Seleccione un SKU para ver los accesorios disponibles
                  </div>
                )}
                {formData.sku_id && (
                  <div className="row">
                    {(filteredAccessories.length > 0 ? filteredAccessories : accessories).map(accessory => (
                      <div key={accessory.id} className="col-md-4 mb-2">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`accessory_${accessory.id}`}
                            checked={formData.details.accessories.includes(accessory.id)}
                            onChange={() => handleAccessoryChange(accessory.id)}
                            disabled={loadingReferences}
                          />
                          <label className="form-check-label" htmlFor={`accessory_${accessory.id}`}>
                            <small>{accessory.accessory_name}</small>
                            <br />
                            <small className="text-muted">{accessory.accessory_type_name}</small>
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Colors */}
            {formData.details.material_id && (
              <div className="mb-3">
                <label className="form-label">
                  <FaPalette className="me-1" />
                  Colores
                </label>
                {filteredColors.length === 0 ? (
                  <div className="alert alert-warning">
                    <FaExclamationTriangle className="me-2" />
                    No hay colores disponibles para el material seleccionado
                  </div>
                ) : (
                  <div className="row">
                    {filteredColors.map(color => (
                      <div key={color.id} className="col-md-3 mb-2">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`color_${color.id}`}
                            checked={formData.details.colors.includes(color.id)}
                            onChange={() => handleColorChange(color.id)}
                          />
                          <label className="form-check-label" htmlFor={`color_${color.id}`}>
                            {color.color_name}
                            {color.color_code && (
                              <span className="badge bg-secondary ms-2" style={{fontSize: '0.7em'}}>
                                {color.color_code}
                              </span>
                            )}
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Productos del Stock Local */}
            <hr className="my-4" />
            
            <div className="mb-3">
              <h6 className="mb-3">
                <FaBoxes className="me-2 text-success" />
                Productos del Stock Local
              </h6>
              
              <div className="alert alert-info mb-3">
                <FaShoppingBasket className="me-2" />
                <small>
                  {!formData.sku_id ? (
                    <strong>💡 Puede crear un ítem SOLO con productos de stock local, sin necesidad de seleccionar un SKU.</strong>
                  ) : (
                    <>Puede agregar productos adicionales del inventario local (accesorios, complementos, etc.) 
                    que se incluirán en este pedido.</>
                  )}
                </small>
              </div>
              
              {errors.stock_products && (
                <div className="alert alert-warning">
                  <FaExclamationTriangle className="me-2" />
                  {errors.stock_products}
                </div>
              )}

              {/* Selector de productos de stock */}
              <div className="row align-items-end mb-3">
                <div className="col-md-6">
                  <label className="form-label">Producto de Stock</label>
                  <select
                    className="form-select"
                    value={selectedStockProduct}
                    onChange={(e) => setSelectedStockProduct(e.target.value)}
                    disabled={loadingStock}
                  >
                    <option value="">
                      {loadingStock ? 'Cargando productos...' : 'Seleccione un producto'}
                    </option>
                    {stockProducts.map(product => (
                      <option key={product.id} value={product.id}>
                        {product.product_name} - {formatCurrency(product.unit_price)}
                        {product.stock_quantity <= 5 && ` (Stock: ${product.stock_quantity})`}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="col-md-3">
                  <label className="form-label">Cantidad</label>
                  <input
                    type="number"
                    className="form-control"
                    value={stockProductQuantity}
                    onChange={(e) => setStockProductQuantity(parseInt(e.target.value) || 1)}
                    min="1"
                    disabled={!selectedStockProduct}
                  />
                </div>
                
                <div className="col-md-3">
                  <button
                    type="button"
                    className="btn btn-success w-100"
                    onClick={handleAddStockProduct}
                    disabled={!selectedStockProduct}
                  >
                    <FaPlus className="me-1" />
                    Agregar
                  </button>
                </div>
              </div>

              {/* Lista de productos de stock agregados */}
              {formData.stock_products.length > 0 && (
                <div className="card">
                  <div className="card-header bg-light">
                    <strong>Productos de Stock Agregados</strong>
                  </div>
                  <div className="card-body p-0">
                    <table className="table table-sm mb-0">
                      <thead>
                        <tr>
                          <th>Producto</th>
                          <th className="text-center" style={{width: '120px'}}>Cantidad</th>
                          <th className="text-end" style={{width: '120px'}}>Precio Unit.</th>
                          <th className="text-end" style={{width: '120px'}}>Subtotal</th>
                          <th className="text-center" style={{width: '80px'}}>Acción</th>
                        </tr>
                      </thead>
                      <tbody>
                        {formData.stock_products.map((sp) => (
                          <tr key={sp.product_id}>
                            <td>{sp.product_name}</td>
                            <td className="text-center">
                              <input
                                type="number"
                                className="form-control form-control-sm text-center"
                                value={sp.quantity}
                                onChange={(e) => handleStockProductQuantityChange(sp.product_id, e.target.value)}
                                min="1"
                                style={{width: '80px', display: 'inline-block'}}
                              />
                            </td>
                            <td className="text-end">{formatCurrency(sp.unit_price)}</td>
                            <td className="text-end">
                              <strong className="text-success">
                                {formatCurrency(sp.unit_price * sp.quantity)}
                              </strong>
                            </td>
                            <td className="text-center">
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleRemoveStockProduct(sp.product_id)}
                                title="Quitar producto"
                              >
                                <FaTrash />
                              </button>
                            </td>
                          </tr>
                        ))}
                        <tr className="table-light">
                          <td colSpan="3" className="text-end"><strong>Subtotal Stock Local:</strong></td>
                          <td className="text-end">
                            <strong className="text-success">
                              {formatCurrency(formData.stock_products.reduce((sum, sp) => 
                                sum + (sp.unit_price * sp.quantity), 0
                              ))}
                            </strong>
                          </td>
                          <td></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="card-footer">
            <div className="d-flex justify-content-between align-items-center">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onCancel}
              >
                Cancelar
              </button>
              
              <div className="d-flex align-items-center">
                {totalPrice > 0 && (
                  <div className="me-3 text-end">
                    {formData.stock_products.length > 0 && (
                      <>
                        <div>
                          <small className="text-muted">Producto: </small>
                          <span>{formatCurrency((parseFloat(formData.sale_price) || 0) * (parseInt(formData.quantity) || 0))}</span>
                        </div>
                        <div>
                          <small className="text-muted">Stock Local: </small>
                          <span className="text-success">
                            {formatCurrency(formData.stock_products.reduce((sum, sp) => 
                              sum + (sp.unit_price * sp.quantity), 0
                            ))}
                          </span>
                        </div>
                        <hr className="my-1" />
                      </>
                    )}
                    <div>
                      <strong>Total: {formatCurrency(totalPrice)}</strong>
                    </div>
                  </div>
                )}
                <button
                  type="submit"
                  className="btn btn-primary"
                >
                  <FaPlus className="me-2" />
                  {isEditing ? 'Actualizar Ítem' : 'Agregar Ítem'}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default OrderItemBuilder;


